import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useNavigate } from 'react-router-dom';
import { Box, DropdownMenu, TableList, TableListActions, Typography, useOverlayContext, withBiContainer } from 'seagull';
import { Copy, Edit, Eye, Trash } from 'seagull/icons';
import { useIoContext, useStore } from '~/contexts';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { useDuplicateDraft } from '~/hooks/reactQuery/mutations/useDuplicateDraft';
import { usePreviewDraft } from '~/hooks/usePreviewDraft';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { useLocaleKeys } from '~/i18n';
import { timeModel } from '~/models';
import { timeTextsPath } from '~/models/time/selectors';
import { selectors } from '~/store/state-selectors';
import { ArchivedProposalsDashboardTestIds } from '../ArchivedProposalsDashboard.types';
import { getUICompanyName } from '../ArchivedProposalsDashboard.utils';
import { useArchivedProposals } from '../hooks/useArchivedProposals';
import { useUnarchiveDraft } from '../hooks/useUnarchiveDraft';
import styles from './ArchivedProposalRow.module.scss';
export const ArchivedProposalRow = withBiContainer((props)=>({
        name: 'archived_proposal_row',
        data: {
            draftId: props.draft.id
        }
    }))((props)=>{
    var _draft_lastModifiedBy, _draft_lastModifiedBy1;
    const worksAt = useStore(selectors.user.business);
    const { t: archivedTexts } = useLocaleKeys('pages.archivedProposalsDashboard.actions');
    const { t: timeTexts } = useLocaleKeys(timeTextsPath);
    const { getNow } = useIoContext();
    const { contactsMap, sampleContact } = useContactsMap(worksAt === null || worksAt === void 0 ? void 0 : worksAt.id);
    const { refresh } = useArchivedProposals();
    const { draft, index } = props;
    const { showConfirmAction } = useOverlayContext();
    const draftLabel = getUICompanyName(draft);
    const navigate = useNavigate();
    const { duplicateDraft } = useDuplicateDraft({
        onSuccess: (newDraftId)=>navigate("/draft/".concat(newDraftId)),
        biCode: 18803
    });
    const { unarchiveDraft } = useUnarchiveDraft({
        onSuccess: ()=>navigate("/draft/".concat(draft.id)),
        biCode: 18802
    });
    const { previewDraft } = usePreviewDraft({
        sampleContact,
        contactsMap
    });
    const { terminateDraftById } = useTerminateAgreement();
    const draftSubLabel = draft.title;
    const handlePreviewDraft = ()=>{
        previewDraft(draft.id);
    };
    const showDuplicateConfirmModal = async ()=>{
        showConfirmAction({
            biName: 'duplicate_archived_proposal_confirm',
            onConfirm: ()=>{
                duplicateDraft(draft.id);
            },
            title: archivedTexts.duplicateArchived(),
            content: archivedTexts.duplicateModalContent(),
            primaryButtonLabel: archivedTexts.continueButton(),
            secondaryButtonLabel: archivedTexts.cancelButton()
        });
    };
    const showEditConfirmModal = async ()=>{
        showConfirmAction({
            biName: 'edit_archived_proposal_confirm',
            onConfirm: ()=>{
                unarchiveDraft(draft.id);
            },
            title: archivedTexts.editArchived(),
            content: archivedTexts.editModalContent(),
            primaryButtonLabel: archivedTexts.continueButton(),
            secondaryButtonLabel: archivedTexts.cancelButton()
        });
    };
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: draft.id,
        testId: ArchivedProposalsDashboardTestIds.Row.Root(index),
        items: [
            {
                value: /*#__PURE__*/ _jsxs(Box, {
                    className: styles.companyName,
                    children: [
                        draftLabel && /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            weight: "normal",
                            testId: ArchivedProposalsDashboardTestIds.Row.CompanyName(index),
                            children: draftLabel
                        }),
                        /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            variant: "paragraph2",
                            skin: "secondary",
                            testId: ArchivedProposalsDashboardTestIds.Row.DraftName(index),
                            children: [
                                "(",
                                draftSubLabel || archivedTexts.unnamedDraft(),
                                ")"
                            ]
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    testId: ArchivedProposalsDashboardTestIds.Row.ArchivedBy(index),
                    children: "".concat((_draft_lastModifiedBy = draft.lastModifiedBy) === null || _draft_lastModifiedBy === void 0 ? void 0 : _draft_lastModifiedBy.firstName, " ").concat((_draft_lastModifiedBy1 = draft.lastModifiedBy) === null || _draft_lastModifiedBy1 === void 0 ? void 0 : _draft_lastModifiedBy1.lastName)
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    testId: ArchivedProposalsDashboardTestIds.Row.ArchivedDate(index),
                    children: timeModel.formatTimeAgo(draft.lastModifiedOn, timeTexts, getNow().getTime())
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            bi: {
                button: {
                    name: 'archived_proposal_view'
                },
                menuTrigger: {
                    name: 'archived_proposal_actions'
                }
            },
            testId: ArchivedProposalsDashboardTestIds.Row.Actions.Root(index),
            children: [
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'preview'
                    },
                    onClick: handlePreviewDraft,
                    iconStart: /*#__PURE__*/ _jsx(Eye, {}),
                    testId: ArchivedProposalsDashboardTestIds.Row.Actions.Preview(index),
                    children: archivedTexts.preview()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'edit'
                    },
                    onClick: ()=>showEditConfirmModal(),
                    iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                    testId: ArchivedProposalsDashboardTestIds.Row.Actions.Edit(index),
                    children: archivedTexts.edit()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'duplicate'
                    },
                    onClick: ()=>showDuplicateConfirmModal(),
                    iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                    testId: ArchivedProposalsDashboardTestIds.Row.Actions.Duplicate(index),
                    children: archivedTexts.duplicate()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'delete'
                    },
                    onClick: (_e, context)=>terminateDraftById(draft.id, ()=>refresh(), context),
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    testId: ArchivedProposalsDashboardTestIds.Row.Actions.Delete(index),
                    children: archivedTexts.delete()
                })
            ]
        })
    }, draft.id);
});
